/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { TFunction } from "i18next";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavigateFunction, useNavigate } from "react-router-dom";

import ConfirmDialog from "~/components/modal/ConfirmDialog";
import { selectUnsavedChangesData } from "~/features/unsavedChanges/selectors";
import { UnsavedChangesActions } from "~/features/unsavedChanges/slice";
import { UnsavedChangesType } from "~/features/unsavedChanges/types";
import { AppDispatch } from "~/state/types";

const getUnsavedChangesConfig = (
  t: TFunction,
  dispatch: AppDispatch,
  navigate: NavigateFunction,
  type: UnsavedChangesType,
  link?: string,
) => {
  return {
    title: `${t("confirm_modal.SAVE_CHANGES", { ns: "common" })}?`,
    content: t("confirm_modal.SAVE_CHANGES_TEXT", { ns: "common" }),
    denyLabel: t("confirm_modal.LEAVE_PAGE", { ns: "common" }),
    confirmLabel: t("confirm_modal.STAY_ON_PAGE", { ns: "common" }),
    confirmClick: () => dispatch(UnsavedChangesActions.closeModal()),
    denyClick: () => {
      dispatch(UnsavedChangesActions.clearState());
      void navigate(link as string);
    },
    enforceOverflow: true,
  };
};

const UnsavedChanges: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const unsavedChangesData = useSelector(selectUnsavedChangesData);

  if (!unsavedChangesData.showModal || !unsavedChangesData.isDirty) {
    return null;
  }

  const config = getUnsavedChangesConfig(
    t,
    dispatch,
    navigate,
    unsavedChangesData.type,
    unsavedChangesData.navigateLink,
  );

  const handleConfirm = () => {
    config.confirmClick();
  };

  const handleDeny = () => {
    config.denyClick();
  };

  const isOpen = unsavedChangesData.showModal && unsavedChangesData.isDirty;

  return (
    <ConfirmDialog
      title={config.title}
      denyLabel={config.denyLabel}
      confirmLabel={config.confirmLabel}
      content={config.content}
      denyClick={handleDeny}
      open={isOpen}
      confirmClick={handleConfirm}
      enforceOverflow={config.enforceOverflow}
    />
  );
};

export default UnsavedChanges;
